import { Updater } from 'immerable/common';
import { useReactiveVar } from '@apollo/client';
import { makeImmerVar } from 'immerable/makeImmerVar';
import { useAsyncEffect } from 'react-utils/hooks/useAsyncEffect';
import { waitForConnectedMagpie } from '../utils/magpie';
import { MagpieConnection } from 'magpie-lib/connection';
import { getDeferred } from '../utils/common';
import {
  StateCommands,
  StateMessages,
  stateTopicDef,
} from '../messaging/topics/state';
import { ValueOf } from '../types/common';
import { useReactiveVarWithSelector } from 'immerable/useReactiveVarWithSelector';

export const FrameStateTypes = {
  LEADER: 'LEADER',
  FOLLOWER: 'FOLLOWER',
} as const;

type FrameStateMeta = {
  isSyncedWithLeader: boolean;
  type: ValueOf<typeof FrameStateTypes>;
};

const isLeader = (stateType: ValueOf<typeof FrameStateTypes>) => {
  return stateType === FrameStateTypes.LEADER;
};

export const makeFrameState = <StateName extends string = string, T = any>(
  stateType: ValueOf<typeof FrameStateTypes>,
  stateName: StateName,
  initialState: T
) => {
  const stateVar = makeImmerVar(initialState);
  const metaVar = makeImmerVar({
    isSyncedWithLeader: isLeader(stateType),
    type: stateType,
  } as FrameStateMeta);

  type FrameStateSubscriber = (event: {
    state: T;
    isStateSync: boolean;
  }) => void;

  const stateSubscribers = [] as FrameStateSubscriber[];

  //Helpers

  const subscribeToStateUpdate = (subscriber: FrameStateSubscriber) => {
    stateSubscribers.push(subscriber);
    return () => {
      stateSubscribers.splice(stateSubscribers.indexOf(subscriber), 1);
    };
  };

  const updateState = (updater: Updater<T>, { isStateSync = false } = {}) => {
    stateVar(updater);
    stateSubscribers.forEach((subscriber) => {
      subscriber({ isStateSync, state: stateVar() });
    });
  };

  const syncState = (updater: Updater<T>) => {
    updateState(updater, { isStateSync: true });
  };

  const isMessageForState = (message: { payload: { stateName: string } }) => {
    return message.payload.stateName === stateName;
  };

  const useSyncState = () => {
    useAsyncEffect(async () => {
      await waitForConnectedMagpie();
      const connection = MagpieConnection.get();
      const topic = connection.useTopic(stateTopicDef);
      // Sync state from external state updates
      const unsubscribeSyncStateMessage = topic.on(
        StateMessages.syncedStateMessage.getType(),
        (message) => {
          if (!isMessageForState(message)) {
            return;
          }
          const { state } = message.payload;
          syncState(state);
        }
      );
      const unsubscribeStateUpdate = subscribeToStateUpdate(
        ({ isStateSync, state }) => {
          // Don't publish a message for updates to state from a sync message otherwise we'll cause a loop
          if (isStateSync) {
            return;
          }
          const message = StateMessages.syncedStateMessage.create({
            state,
            stateName,
          });
          connection.useTopic(stateTopicDef).publish(message);
        }
      );
      return () => {
        unsubscribeSyncStateMessage();
        unsubscribeStateUpdate();
      };
    }, []);
  };

  const executeSyncFromLeaderStateCommand = async () => {
    await waitForConnectedMagpie();
    const deferred = getDeferred<T>();
    const connection = MagpieConnection.get();
    const topic = connection.useTopic(stateTopicDef);
    const command = StateCommands.syncFromLeaderStateCommand.create({
      stateName,
    });
    topic.publish(command, {
      onReply: (reply) => {
        deferred.resolve(reply.payload.state as T);
      },
      onError: (error) => {
        deferred.reject(error);
      },
    });
    return deferred.promise;
  };

  // PUBLIC

  const setState = (updater: Updater<T>) => {
    updateState(updater);
  };

  const getState = () => {
    return stateVar();
  };

  const useFrameState = () => {
    const frameState = useReactiveVar(stateVar);
    return frameState;
  };

  const useFrameStateWithSelector = <R>(selector: (state: T) => R) => {
    const selectedFrameState = useReactiveVarWithSelector(stateVar, selector);
    return selectedFrameState;
  };

  const useMetaState = () => {
    const metaState = useReactiveVar(metaVar);
    return metaState;
  };

  const useSyncFollowerState = () => {
    useSyncState();
    useAsyncEffect(async () => {
      const state = await executeSyncFromLeaderStateCommand();
      syncState(state);
      metaVar((draft) => {
        draft.isSyncedWithLeader = true;
      });
    }, []);
  };

  const useSyncLeaderState = () => {
    useSyncState();
    useAsyncEffect(async () => {
      await waitForConnectedMagpie();
      const connection = MagpieConnection.get();
      const topic = connection.useTopic(stateTopicDef);
      const unsubscribeSyncFromLeaderStateCommand = topic.on(
        StateCommands.syncFromLeaderStateCommand.getType(),
        (message, { reply }) => {
          if (!isMessageForState(message)) {
            return;
          }
          const replyMessage =
            StateMessages.syncedFromLeaderStateMessage.create({
              state: getState(),
              stateName,
            });
          reply(replyMessage);
        }
      );
      return () => {
        unsubscribeSyncFromLeaderStateCommand();
      };
    }, []);
  };

  const frameState = {
    setState,
    getState,
    hooks: {
      useState: useFrameState,
      useStateWithSelector: useFrameStateWithSelector,
      useMetaState,
      useSyncState: isLeader(stateType)
        ? useSyncLeaderState
        : useSyncFollowerState,
    },
  };

  return frameState;
};

export const frameStateBuilder = <
  StateName extends string = string,
  InitialState = any,
>(
  config: {
    stateType?: ValueOf<typeof FrameStateTypes>;
    stateName?: StateName;
    initialState?: InitialState;
  } = {}
) => {
  const { stateType, stateName, initialState } = config;

  return {
    setStateType(newStateType: ValueOf<typeof FrameStateTypes>) {
      return frameStateBuilder({
        stateType: newStateType,
        stateName,
        initialState,
      });
    },

    setStateName<NewStateName extends string>(newStateName: NewStateName) {
      return frameStateBuilder({
        stateType,
        stateName: newStateName,
        initialState,
      });
    },

    setInitialState<NewInitialState extends InitialState>(
      newInitialState: NewInitialState
    ) {
      return frameStateBuilder({
        stateType,
        stateName,
        initialState: newInitialState,
      });
    },

    build() {
      if (!stateType || !stateName || !initialState) {
        throw new Error('State type, name, and initial state are required');
      }
      return makeFrameState(stateType, stateName, initialState);
    },
  };
};
